import { Button, Css, Tooltip, useComputed, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { ReactNode, useCallback } from "react";
import { fail, queryResult } from "src/utils";
import {
  SelectConfigurationStep_StructureFragment,
  useSelectConfigurationStep_SelectedConfigQuery,
} from "../1.SelectConfigurationStep.generated";
import { ApplyConfigurationFormInput } from "../applyConfigurationForm";
import { clearSelectedConfiguration } from "../utils";
import { ApplyConfigurationCard } from "./ApplyConfigurationCard";
import { ApplyConfigurationCard_ReadyPlanConfigLikeFragment } from "./ApplyConfigurationCard.generated";
import { NoConfigurationEmptyState } from "./NoConfigurationEmptyState";
import { SelectConfigurationModal } from "./SelectConfigurationModal";

type ConfigurationChangeSectionProps = {
  formState: ObjectState<ApplyConfigurationFormInput>;
  structure: SelectConfigurationStep_StructureFragment;
};

export function ConfigurationChangeSection(props: ConfigurationChangeSectionProps) {
  const { formState, structure } = props;
  const selectedConfigId = useComputed(() => formState.selectedConfigId.value, [formState]);
  const query = useSelectConfigurationStep_SelectedConfigQuery({
    variables: { configId: selectedConfigId! },
    skip: !selectedConfigId,
  });
  if (!selectedConfigId) {
    return <ConfigurationChangeSectionView formState={formState} structure={structure} />;
  } else {
    return queryResult(query, ({ readyPlanConfigLike }) => (
      <ConfigurationChangeSectionView
        formState={formState}
        structure={structure}
        replacementConfig={readyPlanConfigLike}
      />
    ));
  }
}

type ConfigurationChangeSectionViewProps = ConfigurationChangeSectionProps & {
  replacementConfig?: ApplyConfigurationCard_ReadyPlanConfigLikeFragment;
};
function ConfigurationChangeSectionView(props: ConfigurationChangeSectionViewProps) {
  const { formState, structure, replacementConfig } = props;
  const originalConfig = structure.readyPlanConfig;
  const { openModal } = useModal();
  const configType = useComputed(() => formState.selectedConfigType.value, [formState]);

  const chooseReplacement = useCallback(() => {
    openModal({
      size: "xxl",
      content: (
        <SelectConfigurationModal
          configType={configType ?? fail("No configuration type selected")}
          structure={structure}
          initialSelectedConfigId={replacementConfig?.id}
          onSelect={(config) => {
            formState.set({
              selectedConfigId: config.id,
              readyPlanId: config.readyPlan?.id,
              proposedReadyPlanOptions: config.readyPlanOptions.map((rpo) => ({ readyPlanOptionId: rpo.id })),
              checkoutConfigId: config.__typename === "CheckoutConfig" ? config.id : undefined,
            });
          }}
        />
      ),
    });
  }, [openModal, formState, structure, replacementConfig, configType]);

  if (!originalConfig && !replacementConfig) {
    return <NoConfigurationEmptyState onClick={chooseReplacement} disabled={!formState.selectedConfigType.value} />;
  } else {
    return (
      <div css={Css.df.gap3.mb3.$}>
        <ConfigurationPanel title="Original">
          {originalConfig ? (
            <ApplyConfigurationCard structure={structure} readyPlanConfig={originalConfig} />
          ) : (
            <NoConfigurationEmptyState compact />
          )}
        </ConfigurationPanel>

        <ConfigurationPanel title="Replacement">
          {replacementConfig ? (
            <ApplyConfigurationCard
              structure={structure}
              readyPlanConfig={replacementConfig}
              onClick={chooseReplacement}
              onRemove={() => clearSelectedConfiguration(formState)}
            />
          ) : (
            <ChooseReplacementButton disabled={!configType} onClick={chooseReplacement} />
          )}
        </ConfigurationPanel>
      </div>
    );
  }
}

function ConfigurationPanel(props: { title: string; children: ReactNode }) {
  const { title, children } = props;
  return (
    <div css={Css.df.fdc.gap2.fb0.fg1.$}>
      <span css={Css.mdSb.$}>{title}</span>
      <div css={Css.df.fg1.$}>{children}</div>
    </div>
  );
}

function ChooseReplacementButton(props: { disabled: boolean; onClick: () => void }) {
  const { disabled, onClick } = props;
  return (
    <div css={Css.df.aic.jcc.w100.ba.bcGray300.br8.bgWhite.p3.$}>
      <Tooltip title="Select the configuration to apply to this structure">
        <Button label="Choose Replacement" variant="secondary" disabled={disabled} onClick={onClick} />
      </Tooltip>
    </div>
  );
}
